// src/pages/settings/invoice/InvoiceSettingsDataPage.tsx
import {
  Alert,
  Box,
  Card,
  Field,
  Flex,
  Select,
  SimpleGrid,
  Tag,
  TagLabel,
  TagRoot,
  Text,
  VStack,
  chakra,
  createListCollection,
} from '@chakra-ui/react'
import { LuBook, LuBookUser, LuUser } from 'react-icons/lu'
import { useInvoiceSettingsCtx } from './InvoiceSettingsShell'
import { SettingsActionBar } from '../SettingsActionBar'
import { SettingsInput } from '../SettingsControls'
import { useUserStore } from '@/stores/user.store'
import { normaliseVatId } from '@/common/invoiceSettings.schema'
import { FormLayout } from '@/components/layout/FormLayout'
import React, { useEffect, useRef } from 'react'
type IssuerType = 'COMPANY' | 'PERSON' | 'COMPANY_PERSON'

const MIGRATION_VERSION = 'v1'
const MIGRATION_DONE_KEY = `sib_invoice_settings_migration_done_${MIGRATION_VERSION}`
const MIGRATION_SETTINGS_CONFIRMED = 'sib_invoice_settings_confirmed'
function requiredMark(isRequired: boolean) {
  return isRequired ? <Field.RequiredIndicator /> : null
}

export function InvoiceSettingsDataPage() {
  const { saving, serverErrors, clearServerError, formFormik, hasFormChanged, saveForm } =
    useInvoiceSettingsCtx()
  const didSyncInitialRef = useRef(false)

  useEffect(() => {
    if (didSyncInitialRef.current) return

    // After loadData(), Shell does setValues(safeRest).
    // Formik still thinks initialValues are the empty defaults => dirty=true,
    // but compared to initialForm (from backend) there are no changes => hasFormChanged=false.
    if (formFormik.dirty && !hasFormChanged) {
      formFormik.resetForm({ values: formFormik.values })
    }

    didSyncInitialRef.current = true
  }, [formFormik.dirty, hasFormChanged, formFormik.values, formFormik])

  const user = useUserStore(s => s.user)
  const confirmed = localStorage.getItem(MIGRATION_SETTINGS_CONFIRMED) === '1'
  const showMigrationBanner = !!user && !confirmed
  const needsConfirmation = showMigrationBanner

  const issuerType: IssuerType = (formFormik.values.issuerType as IssuerType) ?? 'PERSON'

  const isCompanyMode = issuerType === 'COMPANY'
  const isPersonMode = issuerType === 'PERSON'
  const isMixedMode = issuerType === 'COMPANY_PERSON'

  const companyRequired = isCompanyMode || isMixedMode
  const personRequired = isPersonMode || isMixedMode
  // At least one tax identifier is required once the invoice carries VAT (EN 16931 BR-S-02).
  const taxIdRequired = Number(formFormik.values.vatPercentage) > 0

  const companyDisabled = isPersonMode
  const personDisabled = isCompanyMode

  async function setIssuer(next: IssuerType) {
    formFormik.setFieldValue('issuerType', next)
    await touchAndValidate('issuerType')

    // compute required fields after switch
    const companyRequiredNow = next === 'COMPANY' || next === 'COMPANY_PERSON'
    const personRequiredNow = next === 'PERSON' || next === 'COMPANY_PERSON'

    if (companyRequiredNow) await touchAndValidate('company')
    if (personRequiredNow) {
      await touchAndValidate('firstName')
      await touchAndValidate('lastName')
    }

    // if a field becomes disabled, clear its errors so it doesn't block form validity
    if (next === 'COMPANY') {
      formFormik.setFieldTouched('firstName', false, false)
      formFormik.setFieldTouched('lastName', false, false)
      formFormik.setFieldError('firstName', undefined)
      formFormik.setFieldError('lastName', undefined)
      clearServerError('firstName' as any)
      clearServerError('lastName' as any)
    }

    if (next === 'PERSON') {
      formFormik.setFieldTouched('company', false, false)
      formFormik.setFieldError('company', undefined)
      clearServerError('company' as any)
    }
  }

  const countryCollection = createListCollection({
    items: [
      { label: 'Deutschland', value: 'de' },
      { label: 'Schweiz', value: 'ch' },
      { label: 'Österreich', value: 'at' },
    ],
  })
  // function isInvalid(name: string) {
  type InvoiceSettingsField = keyof typeof formFormik.values

  function isInvalid(name: InvoiceSettingsField) {
    return !!(
      formFormik.touched?.[name] &&
      ((serverErrors as any)?.[name] || (formFormik.errors as any)?.[name])
    )
  }

  // function errorText(name: string) {
  function errorText(name: InvoiceSettingsField) {
    return (serverErrors as any)?.[name] || (formFormik.errors as any)?.[name]
  }

  // function onTextChange(name: string) {
  function onTextChange(name: InvoiceSettingsField) {
    return async (e: React.ChangeEvent<HTMLInputElement>) => {
      formFormik.handleChange(e)
      clearServerError(name as any)

      //  inline validation:
      await touchAndValidate(name)
    }
  }
  function touchField(name: string) {
    formFormik.setFieldTouched(name as any, true, false) // don't validate here, we validate explicitly
  }

  async function validateField(name: string) {
    // validateField returns error string | undefined depending on Formik version
    await formFormik.validateField(name as any)
  }

  // Fields whose validity depends on a sibling. Validating only the edited field leaves the
  // sibling's error stale — clear the VAT ID and the tax number would still claim "your
  // Steuernummer is in the VAT field", because nothing re-ran its check.
  const CROSS_FIELD_DEPENDENTS: Record<string, string[]> = {
    vatId: ['taxNumber'],
    taxNumber: ['vatId'],
    vatPercentage: ['taxNumber', 'vatId'],
  }

  async function touchAndValidate(name: string) {
    touchField(name)
    await validateField(name)

    // Refresh the counterparts without touching them: an already-touched sibling gets its
    // stale error cleared, an untouched one stays quiet rather than shouting about a field
    // the user has not reached yet.
    for (const dependent of CROSS_FIELD_DEPENDENTS[name] ?? []) {
      await validateField(dependent)
    }
  }

  // Cosmetic only: shows the user, as they leave the field, the value that will be stored.
  // The guarantee lives in saveForm, which normalises the payload however the save was
  // triggered — a blur handler cannot be load-bearing, since Enter-to-submit never fires it.
  async function onVatIdBlur(e: React.FocusEvent<HTMLInputElement>) {
    const normalised = normaliseVatId(e.target.value)
    if (normalised !== e.target.value) {
      await formFormik.setFieldValue('vatId', normalised)
    }
    formFormik.handleBlur(e)
    await touchAndValidate('vatId')
  }
  const showActionBar = (formFormik.dirty && hasFormChanged) || needsConfirmation
  const saveDisabled = !formFormik.isValid || saving || !showActionBar
  async function handleSave() {
    try {
      await saveForm({ force: needsConfirmation })

      // Mark migration as completed permanently
      localStorage.setItem(MIGRATION_DONE_KEY, '1')
      localStorage.setItem(MIGRATION_SETTINGS_CONFIRMED, '1')
    } catch (e) {
      // do nothing – if save fails, migration should NOT be marked as done
      console.error('Save failed, migration not completed', e)
    }
  }

  return (
    <chakra.form
      data-testid="invoice-settings-data"
      onSubmit={e => {
        e.preventDefault()
        handleSave()
      }}
    >
      <fieldset disabled={saving} style={{ width: '100%' }}>
        <FormLayout.Root>
          {/* Header note */}
          <FormLayout.Section>
            <FormLayout.Title>Rechnungsdaten</FormLayout.Title>

            {showMigrationBanner && (
              <Alert.Root status="info">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Title>Warum wir das geändert haben</Alert.Title>
                  <Alert.Description asChild>
                    <Flex direction="column" gap={3}>
                      <Text>
                        Bisher wurden Ihre Registrierungsdaten automatisch als Rechnungssteller auf
                        Ihren erstellten Rechnungen verwendet.
                      </Text>

                      <Text>
                        Um gängigen Abrechnungsstandards zu entsprechen, werden Nutzerdaten und
                        Rechnungsdaten künftig getrennt verwaltet. Damit ist klar definiert:
                      </Text>

                      <Box as="ul" pl={6} style={{ listStyleType: 'disc' }}>
                        <Box as="li">
                          <Text>
                            Abonnement-Daten betreffen ausschließlich die Abrechnung unserer
                            Software (z.&nbsp;B. Vertragslaufzeit und Rechnungsadresse für Ihr
                            Abo).
                          </Text>
                        </Box>

                        <Box as="li">
                          <Text>
                            Rechnungsdaten betreffen ausschließlich die Angaben, die auf den
                            Rechnungen erscheinen, die Sie an Ihre eigenen Kunden stellen.
                          </Text>
                        </Box>
                      </Box>

                      <Text>
                        Durch diese Trennung ist transparent nachvollziehbar, welche Daten welchem
                        Zweck dienen. Ihre bisherigen Angaben wurden übernommen. Bitte prüfen Sie
                        diese und speichern Sie die Daten zur Bestätigung.
                      </Text>
                    </Flex>
                  </Alert.Description>
                </Alert.Content>
              </Alert.Root>
            )}
            <FormLayout.Description>
              Hier hinterlegen Sie die Angaben des Rechnungsstellers – also die Daten, die später
              auf Ihren Rechnungen als Absender erscheinen.
            </FormLayout.Description>
            <FormLayout.Description>
              Das können Ihre persönlichen Daten oder die Daten Ihres Unternehmens sein. Diese
              Informationen werden standardmäßig für alle erstellten Rechnungen verwendet, sofern
              kein abweichender Rechnungssteller definiert ist.
            </FormLayout.Description>
          </FormLayout.Section>

          {/* Rechnungssteller */}
          <Flex direction="column" gap={4}>
            <FormLayout.Title>Rechnungssteller</FormLayout.Title>

            <Field.Root required>
              <Field.Label>
                Rechnungssteller auswählen <Field.RequiredIndicator />
              </Field.Label>
            </Field.Root>

            <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
              <Card.Root
                role="button"
                tabIndex={0}
                onClick={() => setIssuer('COMPANY')}
                onKeyDown={e => e.key === 'Enter' && setIssuer('COMPANY')}
                variant={issuerType === 'COMPANY' ? 'subtle' : 'outline'}
                colorPalette="blue"
                cursor="pointer"
                userSelect="none"
              >
                <Card.Body>
                  <VStack alignContent="center">
                    <LuBook />
                    <Card.Title>Unternehmen</Card.Title>
                    <Card.Description>Nur Firmenname auf der Rechnung</Card.Description>
                  </VStack>
                </Card.Body>
              </Card.Root>

              <Card.Root
                role="button"
                tabIndex={0}
                onClick={() => setIssuer('PERSON')}
                onKeyDown={e => e.key === 'Enter' && setIssuer('PERSON')}
                variant={issuerType === 'PERSON' ? 'subtle' : 'outline'}
                colorPalette="blue"
                cursor="pointer"
                userSelect="none"
              >
                <Card.Body>
                  <VStack alignContent="center">
                    <LuUser />
                    <Card.Title>Privatperson</Card.Title>
                    <Card.Description>Vor- und Nachname auf der Rechnung</Card.Description>
                  </VStack>
                </Card.Body>
              </Card.Root>

              <Card.Root
                role="button"
                tabIndex={0}
                onClick={() => setIssuer('COMPANY_PERSON')}
                onKeyDown={e => e.key === 'Enter' && setIssuer('COMPANY_PERSON')}
                variant={issuerType === 'COMPANY_PERSON' ? 'subtle' : 'outline'}
                colorPalette="blue"
                cursor="pointer"
                userSelect="none"
              >
                <Card.Body>
                  <VStack alignContent="center">
                    <LuBookUser />
                    <Card.Title>Unternehmen + Privatperson</Card.Title>
                    <Card.Description>Firmenname + Vor- und Nachname</Card.Description>
                  </VStack>
                </Card.Body>
              </Card.Root>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
              <Field.Root invalid={isInvalid('company')}>
                <Field.Label>Unternehmen {requiredMark(companyRequired)}</Field.Label>
                <SettingsInput
                  {...formFormik.getFieldProps('company')}
                  disabled={companyDisabled}
                  onChange={onTextChange('company')}
                />
                {isInvalid('company') && <Field.ErrorText>{errorText('company')}</Field.ErrorText>}
              </Field.Root>

              <Field.Root invalid={isInvalid('firstName')}>
                <Field.Label>Vorname {requiredMark(personRequired)}</Field.Label>
                <SettingsInput
                  {...formFormik.getFieldProps('firstName')}
                  disabled={personDisabled}
                  onChange={onTextChange('firstName')}
                />
                {isInvalid('firstName') && (
                  <Field.ErrorText>{errorText('firstName')}</Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={isInvalid('lastName')}>
                <Field.Label>Nachname {requiredMark(personRequired)}</Field.Label>
                <SettingsInput
                  {...formFormik.getFieldProps('lastName')}
                  disabled={personDisabled}
                  onChange={onTextChange('lastName')}
                />
                {isInvalid('lastName') && (
                  <Field.ErrorText>{errorText('lastName')}</Field.ErrorText>
                )}
              </Field.Root>
            </SimpleGrid>
          </Flex>

          {/* Adresse */}
          <Flex direction="column" gap={4}>
            <FormLayout.Title>Adresse</FormLayout.Title>

            <SimpleGrid templateColumns={{ base: '1fr', md: '2fr 1fr 1fr' }} gap={4}>
              <Field.Root invalid={isInvalid('street')} gridColumn={{ base: 'auto', md: '1 / 3' }}>
                <Field.Label>Straße {requiredMark(true)}</Field.Label>
                <SettingsInput
                  {...formFormik.getFieldProps('street')}
                  onChange={onTextChange('street')}
                />
                {isInvalid('street') && <Field.ErrorText>{errorText('street')}</Field.ErrorText>}
              </Field.Root>

              <Field.Root invalid={isInvalid('streetNumber')}>
                <Field.Label>Hausnummer {requiredMark(true)}</Field.Label>
                <SettingsInput
                  {...formFormik.getFieldProps('streetNumber')}
                  onChange={onTextChange('streetNumber')}
                />
                {isInvalid('streetNumber') && (
                  <Field.ErrorText>{errorText('streetNumber')}</Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={isInvalid('postal')}>
                <Field.Label>Postleitzahl {requiredMark(true)}</Field.Label>
                <SettingsInput
                  {...formFormik.getFieldProps('postal')}
                  onChange={onTextChange('postal')}
                />
                {isInvalid('postal') && <Field.ErrorText>{errorText('postal')}</Field.ErrorText>}
              </Field.Root>

              <Field.Root invalid={isInvalid('city')}>
                <Field.Label>Stadt {requiredMark(true)}</Field.Label>
                <SettingsInput
                  {...formFormik.getFieldProps('city')}
                  onChange={onTextChange('city')}
                />
                {isInvalid('city') && <Field.ErrorText>{errorText('city')}</Field.ErrorText>}
              </Field.Root>
              <Field.Root invalid={isInvalid('country')}>
                <Field.Label>Land {requiredMark(true)}</Field.Label>

                <Select.Root
                  name="country"
                  collection={countryCollection}
                  value={formFormik.values.country ? [formFormik.values.country] : []}
                  onValueChange={async details => {
                    const nextValue = details.value[0] ?? ''
                    formFormik.setFieldValue('country', nextValue)
                    clearServerError('country' as any)
                    await touchAndValidate('country')
                  }}
                >
                  <Select.Control>
                    <Select.Trigger>
                      <Select.ValueText placeholder="Bitte auswählen" />
                      <Select.IndicatorGroup>
                        <Select.Indicator />
                      </Select.IndicatorGroup>
                    </Select.Trigger>
                  </Select.Control>

                  <Select.Positioner>
                    <Select.Content>
                      {countryCollection.items.map(opt => (
                        <Select.Item key={opt.value} item={opt}>
                          {opt.label}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Select.Root>

                {isInvalid('country') && <Field.ErrorText>{errorText('country')}</Field.ErrorText>}
              </Field.Root>
            </SimpleGrid>
          </Flex>

          {/* Steuerdaten */}
          <Flex direction="column" gap={4}>
            <FormLayout.Title>Steuerdaten</FormLayout.Title>

            <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
              <Field.Root invalid={isInvalid('taxNumber')}>
                <Field.Label>
                  Steuernummer {requiredMark(taxIdRequired)}
                  {!taxIdRequired && (
                    <TagRoot variant="subtle">
                      <TagLabel>optional</TagLabel>
                    </TagRoot>
                  )}
                </Field.Label>
                <SettingsInput
                  {...formFormik.getFieldProps('taxNumber')}
                  onChange={onTextChange('taxNumber')}
                  placeholder="17/066/10229"
                />
                {isInvalid('taxNumber') ? (
                  <Field.ErrorText>{errorText('taxNumber')}</Field.ErrorText>
                ) : (
                  <Field.HelperText>Vom Finanzamt, z. B. 17/066/10229</Field.HelperText>
                )}
              </Field.Root>

              <Field.Root invalid={isInvalid('vatId')}>
                <Field.Label>
                  Umsatzsteuer-ID {requiredMark(taxIdRequired)}
                  {!taxIdRequired && (
                    <TagRoot variant="subtle">
                      <TagLabel>optional</TagLabel>
                    </TagRoot>
                  )}
                </Field.Label>
                <SettingsInput
                  {...formFormik.getFieldProps('vatId')}
                  onChange={onTextChange('vatId')}
                  onBlur={onVatIdBlur}
                  placeholder="DE123456789"
                />
                {isInvalid('vatId') ? (
                  <Field.ErrorText>{errorText('vatId')}</Field.ErrorText>
                ) : (
                  <Field.HelperText>Beginnt mit dem Länderkürzel, z. B. DE123456789</Field.HelperText>
                )}
              </Field.Root>

              <Field.Root invalid={isInvalid('vatPercentage')}>
                <Field.Label>Mehrwertsteuer {requiredMark(true)}</Field.Label>
                <SettingsInput
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={formFormik.values.vatPercentage ?? ''}
                  onChange={async e => {
                    let raw = e.target.value

                    // normalize decimal
                    raw = raw.replace(',', '.')

                    if (raw === '') {
                      formFormik.setFieldValue('vatPercentage', '')
                      return
                    }

                    // allow only valid number format
                    if (!/^\d*\.?\d*$/.test(raw)) return

                    formFormik.setFieldValue('vatPercentage', Number(raw))

                    clearServerError('vatPercentage' as any)
                    await touchAndValidate('vatPercentage')
                    // Requirement of the tax fields depends on VAT %, so re-check them live.
                    await touchAndValidate('taxNumber')
                    await touchAndValidate('vatId')
                  }}
                />
                {isInvalid('vatPercentage') && (
                  <Field.ErrorText>{errorText('vatPercentage')}</Field.ErrorText>
                )}
              </Field.Root>
            </SimpleGrid>
          </Flex>

          {/* Kontodaten */}
          <Flex direction="column" gap={4}>
            <FormLayout.Title>Kontodaten</FormLayout.Title>

            <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
              <Field.Root invalid={isInvalid('accountHolder')}>
                <Field.Label>Kontoinhaber {requiredMark(true)}</Field.Label>
                <SettingsInput
                  {...formFormik.getFieldProps('accountHolder')}
                  onChange={onTextChange('accountHolder')}
                />
                {isInvalid('accountHolder') && (
                  <Field.ErrorText>{errorText('accountHolder')}</Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={isInvalid('bankName')}>
                <Field.Label>Bank {requiredMark(true)}</Field.Label>
                <SettingsInput
                  {...formFormik.getFieldProps('bankName')}
                  onChange={onTextChange('bankName')}
                />
                {isInvalid('bankName') && (
                  <Field.ErrorText>{errorText('bankName')}</Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={isInvalid('iban')}>
                <Field.Label>IBAN {requiredMark(true)}</Field.Label>
                <SettingsInput
                  value={formFormik.values.iban ?? ''}
                  onChange={async e => {
                    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
                    formFormik.setFieldValue('iban', value)
                    clearServerError('iban' as any)
                    await touchAndValidate('iban')
                  }}
                  autoComplete="off"
                />
                {isInvalid('iban') && <Field.ErrorText>{errorText('iban')}</Field.ErrorText>}
              </Field.Root>

              <Field.Root invalid={isInvalid('bic')}>
                <Field.Label>BIC {requiredMark(true)}</Field.Label>
                <SettingsInput
                  value={formFormik.values.bic ?? ''}
                  onChange={async e => {
                    const value = e.target.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, '')
                      .slice(0, 11)
                    formFormik.setFieldValue('bic', value)
                    clearServerError('bic' as any)
                    await touchAndValidate('bic')
                  }}
                />
                {isInvalid('bic') && <Field.ErrorText>{errorText('bic')}</Field.ErrorText>}
              </Field.Root>
            </SimpleGrid>
          </Flex>

          {/* Kontakt */}
          <Flex direction="column" gap={4}>
            <FormLayout.Title>Kontakt</FormLayout.Title>

            <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
              <Field.Root invalid={isInvalid('email')}>
                <Field.Label>
                  E-Mail Adresse{' '}
                  <Tag.Root variant="subtle">
                    <Tag.Label>Optional</Tag.Label>
                  </Tag.Root>
                </Field.Label>
                <SettingsInput
                  type="email"
                  value={formFormik.values.email ?? ''}
                  width="100%"
                  // onChange={formFormik.handleChange}
                  onChange={async e => {
                    formFormik.setFieldValue('email', e.target.value)
                    clearServerError('email' as any)
                    await touchAndValidate('email')
                  }}
                  onBlur={formFormik.handleBlur}
                />
                {isInvalid('email') && <Field.ErrorText>{errorText('email')}</Field.ErrorText>}
              </Field.Root>

              <Field.Root invalid={isInvalid('phone')}>
                <Field.Label>
                  Telefon{' '}
                  <Tag.Root variant="subtle">
                    <Tag.Label>Optional</Tag.Label>
                  </Tag.Root>
                </Field.Label>
                <SettingsInput
                  type="tel"
                  autoComplete="tel"
                  {...formFormik.getFieldProps('phone')}
                  onChange={onTextChange('phone')}
                />
                {isInvalid('phone') && <Field.ErrorText>{errorText('phone')}</Field.ErrorText>}
              </Field.Root>
            </SimpleGrid>
          </Flex>
        </FormLayout.Root>
        <SettingsActionBar
          visible={showActionBar}
          onCancel={() => formFormik.resetForm()}
          cancelDisabled={saving}
          onSave={() => handleSave()}
          saving={saving}
          saveDisabled={saveDisabled}
        />
      </fieldset>
    </chakra.form>
  )
}
